import React, { useState, useRef, useCallback, useEffect } from 'react';
import api from './api';
import { useMsal } from '@azure/msal-react';
import CameraScan from './CameraScan';
import { useBarcodeScanner } from './useBarcodeScanner';
import { explainReason } from './ReceivingHistoryLog';
import errorReason from './errorReason';

// ── Compress a File/Blob to a base64 JPEG at the given max dimension ──────────
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => {
        if (!blob) return reject(new Error('Canvas toBlob failed'));
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]); // base64
        reader.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// What to tell the receiver when Update PO fails: what went wrong in plain words, then
// the exact text underneath, and a reminder that nothing on screen was lost.
function updateErrorMessage(err, action = '') {
  const data = err.response?.data;
  const raw = (typeof data === 'string' ? data : data?.result || data?.detail || data?.error || data?.title || data?.message)
    || (err.response ? `The server answered with an error (${err.response.status}).` : '')
    || (err.code === 'ECONNABORTED' ? 'The request took too long.' : '')
    || (err.message === 'Network Error' ? 'Could not reach 2Check. Check the Wi-Fi.' : '')
    || err.message
    || 'Unknown error.';
  const plain = explainReason(raw);
  const detail = String(raw).replace(/\s+/g, ' ').trim();
  const shown = plain && plain !== detail ? `${plain}\n\nDetails: ${detail.slice(0, 300)}` : detail.slice(0, 300);
  const lead = action ? `${action}\n\n` : '';
  return `${lead}${shown}\n\nYour scans are still on screen. Nothing was lost.`;
}

export default function ReceivingPage({ user, msalInstance }) {
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [matchingPos, setMatchingPos] = useState(null);
  const [selectedPoId, setSelectedPoId] = useState(null);
  const [finalizingPoId, setFinalizingPoId] = useState(null);
  // Map of poId → { items: [], loading: bool }
  const [poItemsMap, setPoItemsMap] = useState({});
  // Map of poId → { barcodeValue: sessionCount }
  const [sessionScans, setSessionScans] = useState({});
  // barcodeValue of the last scanned item, cleared after highlight timeout
  const [flash, setFlash] = useState({ key: null, n: 0, down: false });
  const highlightTimer = useRef(null);
  const [receivingInput, setReceivingInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [useCamera, setUseCamera] = useState(false);
  const [lastScanned, setLastScanned] = useState({ code: '', timestamp: 0 });
  const [scanHistory, setScanHistory] = useState([]);
  const [cartonPrompt, setCartonPrompt] = useState(null);
  const cameraRef = useRef(null);
  const [locationPrompt, setLocationPrompt] = useState(null);
  const locationInputRef = useRef(null);
  const { instance, accounts } = useMsal();
  const [reportUrl, setReportUrl] = useState(null);
  const [reportUrlLoading, setReportUrlLoading] = useState(true);
  const [setLabel, setSetLabel] = useState('Carton');
  const account = accounts?.[0];
  const camCooldownRef = useRef(false);
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const adjustInputRefs = useRef({});
  const itemRowRefs = useRef({});
  const selectPoRef = useRef(null); // always the latest handleSelectPo, for the camera callback
  const [accessDenied, setAccessDenied] = useState(false);
  const scanHandlerRef = useRef(null);
  const cartonInputRef = useRef(null);
  const [sessionCartonSizes, setSessionCartonSizes] = useState({});
  const [itemToggles, setItemToggles] = useState({});
  const poItemsMapRef = useRef(poItemsMap);
  const sessionScansRef = useRef(sessionScans);
  const itemTogglesRef = useRef(itemToggles);
  const [showWarehouseLocation, setShowWarehouseLocation] = useState(true);
  const selectedPoIdRef = useRef(selectedPoId);
  const [alert, setAlert] = useState(null);
  const [maxPhotosPerItem, setMaxPhotosPerItem] = useState(5);

  // Map of poId → [{ dataUrl, base64, fileName }]
  const [poPhotos, setPoPhotos] = useState({});
  const photoInputRef = useRef(null);
  const photoCapturingPoId = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setAlert({ message, type });
  }, []);

  useEffect(() => { poItemsMapRef.current = poItemsMap; }, [poItemsMap]);
  useEffect(() => { sessionScansRef.current = sessionScans; }, [sessionScans]);
  useEffect(() => { itemTogglesRef.current = itemToggles; }, [itemToggles]);
  useEffect(() => { selectedPoIdRef.current = selectedPoId; }, [selectedPoId]);

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      const token = localStorage.getItem('authToken');
      return { Authorization: `Bearer ${token}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account,
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

useEffect(() => {
  const fetchConfig = async () => {
    try {
      const headers = await getAuthHeaders();

      // Check feature access first — hits the RequiresPoReceivingFeature filter
      try {
        await api.get('/api/receiving/access-check', { headers });
      } catch (err) {
        if (err.response?.status === 402) {
          setAccessDenied(true);
          setReportUrlLoading(false);
          return;
        }
      }

      const res = await api.get('/api/config/credentials', { headers });
      setReportUrl(res.data.reportUrl || null);
      setSetLabel(res.data.setLabel?.split(' ')[0] ?? 'Carton');
      setShowWarehouseLocation(res.data.showWarehouseLocation ?? true);
      setMaxPhotosPerItem(res.data.maxPhotosPerItem ?? 5);
    } catch {
      setReportUrl(null);
    } finally {
      setReportUrlLoading(false);
    }
  };

  const authProvider = localStorage.getItem('authProvider') || 'microsoft';
  if (authProvider === 'google' || account) {
    fetchConfig();
  } else {
    setReportUrlLoading(false);
  }
}, [account]);

  // ── Photo handling ──────────────────────────────────────────────────────────
  const handlePhotoCapture = useCallback((poId) => {
    photoCapturingPoId.current = poId;
    photoInputRef.current?.click();
  }, []);

  const handlePhotoFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const poId = photoCapturingPoId.current;
    if (!poId) return;

    const existing = poPhotos[poId] ?? [];
    const slots = maxPhotosPerItem - existing.length;
    if (slots <= 0) {
      showToast(`Photo limit reached (${maxPhotosPerItem} max).`, 'warning');
      e.target.value = '';
      return;
    }

    const toProcess = files.slice(0, slots);
    if (files.length > slots) {
      showToast(`Only ${slots} photo${slots !== 1 ? 's' : ''} added — limit is ${maxPhotosPerItem}.`, 'warning');
    }
const newPhotos = await Promise.all(
  toProcess.map(async (file, i) => {
    const base64 = await compressImage(file);
    const dataUrl = `data:image/jpeg;base64,${base64}`;
    const poNumber = matchingPos?.find(p => p.id === poId)?.number ?? poId;
    const date = new Date().toISOString().slice(0, 10); // "2026-06-10"
    const time = new Date().toTimeString().slice(0, 8).replace(/:/g, '-'); // "14-32-05"
    const fileName = `PO-${poNumber}-delivery-${date}-${time}${toProcess.length > 1 ? `-${i + 1}` : ''}.jpg`;
    return { dataUrl, base64, fileName };
  })
);

    setPoPhotos(prev => ({
      ...prev,
      [poId]: [...(prev[poId] ?? []), ...newPhotos],
    }));

    e.target.value = '';
  }, [poPhotos, maxPhotosPerItem, showToast]);

  const handleRemovePhoto = useCallback((poId, idx) => {
    setPoPhotos(prev => ({
      ...prev,
      [poId]: (prev[poId] ?? []).filter((_, i) => i !== idx),
    }));
  }, []);

  // Brief flash on an item's row so the change is easy to spot: green for more, red for less.
  // Same wash as the Sales Order table: a/b alternate so two quick scans of one item restart it.
  const flashItem = useCallback((key, down = false) => {
    setFlash(f => ({ key, n: f.n + 1, down }));
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setFlash(f => ({ ...f, key: null })), 900);
  }, []);

  // ── Scan logic ──────────────────────────────────────────────────────────────
  const completeScan = useCallback((matchedItem, key, sessionCount, increment) => {
    const alreadyReceived = matchedItem.scannedCount + sessionCount;
    const totalAfter = alreadyReceived + increment;

    if (totalAfter > matchedItem.quantity) {
      if (alreadyReceived >= matchedItem.quantity) {
        showToast(`"${matchedItem.barcode}" is already fully received.`, 'warning');
      } else {
        const remaining = matchedItem.quantity - alreadyReceived;
        showToast(
          `Scanning a ${setLabel.toLowerCase()} of ${increment} would exceed the ordered qty. ` +
          `Only ${remaining} unit${remaining !== 1 ? 's' : ''} remaining.`,
          'warning'
        );
      }
      return;
    }

    setSessionScans(prev => {
      const poScans = prev[selectedPoId] ?? {};
      return {
        ...prev,
        [selectedPoId]: {
          ...poScans,
          [key]: (poScans[key] ?? 0) + increment,
        },
      };
    });

    setScanHistory(prev => [...prev, { poId: selectedPoId, key, increment }]);
    flashItem(key);
  }, [selectedPoId, setLabel, flashItem]);

  const handleReceivingScan = useCallback((code) => {
    const selectedPoId = selectedPoIdRef.current;
    if (!selectedPoId) return;

    const poItems = poItemsMapRef.current[selectedPoId]?.items ?? [];
    if (poItems.length === 0) return;

    const normalize = s => s?.trim().toUpperCase().replace(/^0+/, '') ?? '';
    const cleanScan = normalize(code);

    const matchedItem = poItems.find(item =>
      normalize(item.barcodeValue) === cleanScan ||
      normalize(item.barcode) === cleanScan
    );

    if (!matchedItem) {
      showToast(`Item "${code}" not found on this PO.`, 'error');
      return;
    }

    const key = matchedItem.barcodeValue || matchedItem.barcode;
    // Bring the scanned item into view so nobody has to hunt for it in a long PO.
    itemRowRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const sessionCount = sessionScansRef.current[selectedPoId]?.[key] ?? 0;
    const mode = itemTogglesRef.current[key] ?? 'single';

    if (mode === 'carton' && matchedItem.cartonSize == null) {
      setCartonPrompt({ item: matchedItem, key, sessionCount });
      return;
    }

    const increment = mode === 'single' ? 1 : (matchedItem.cartonSize ?? 1);
    completeScan(matchedItem, key, sessionCount, increment);
  }, [completeScan]);

  const handleCamScan = useCallback(async (code) => {
    if (camCooldownRef.current) return;

    const now = Date.now();
    if (code === lastScanned.code && now - lastScanned.timestamp < 1000) return;
    setLastScanned({ code, timestamp: now });

    camCooldownRef.current = true;
    setTimeout(() => { camCooldownRef.current = false; }, 1500);

    if (selectedPoId) {
      handleReceivingScan(code);
    } else {
      setScannedBarcode(code);
      if (code.trim()) {
        setLoading(true);
        try {
          const headers = await getAuthHeaders();
          const res = await api.post('/api/receiving/find-pos', { barcode: code }, { headers });
          if (!res.data.found) {
            setMatchingPos([]);
          } else {
            const pos = res.data.pos.map(po => ({
              ...po,
              id: po.id,
              number: po.number,
              vendor: po.vendorName,
              itemCount: po.lineItems.length,
              status: po.lineItems.every(li => li.qtyReceived >= li.qtyOrdered)
                ? 'received'
                : po.lineItems.some(li => li.qtyReceived > 0)
                ? 'partial'
                : 'open',
            }));
            setMatchingPos(pos);
          }
          setSelectedPoId(null);
          setPoItemsMap({});
          // Same as typing: a single matching PO opens straight away.
          if (res.data.found && res.data.pos.length === 1) {
            const only = res.data.pos[0];
            selectPoRef.current?.({ ...only, id: only.id, number: only.number }, true);
          }
        } catch (err) {
          console.error('Failed to find POs:', err);
          showToast(updateErrorMessage(err, 'Searching for POs failed.'));
        } finally {
          setLoading(false);
        }
      }
    }
  }, [selectedPoId, handleReceivingScan]);

  useEffect(() => {
    scanHandlerRef.current = selectedPoId
      ? (code) => handleReceivingScan(code)
      : (code) => setScannedBarcode(code);
  }, [selectedPoId, handleReceivingScan]);

  useEffect(() => {
    const SCAN_RESET_TIME = 300;

    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const now = Date.now();
      if (now - lastKeyTimeRef.current > SCAN_RESET_TIME) bufferRef.current = '';
      lastKeyTimeRef.current = now;

      if (e.key === 'Enter' || e.key === 'Tab' || e.code === 'NumpadEnter') {
        if (bufferRef.current.length > 0) {
          const code = bufferRef.current;
          requestAnimationFrame(() => scanHandlerRef.current(code));
          bufferRef.current = '';
        }
        return;
      }

      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key.length === 1) bufferRef.current += e.key;
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── API actions ─────────────────────────────────────────────────────────────
  const handleUndo = () => {
    // Several POs can hold unsent scans at once, so undo the last scan on the open PO.
    let idx = -1;
    scanHistory.forEach((h, i) => { if (h.poId === selectedPoId) idx = i; });
    if (idx < 0) return;

    const last = scanHistory[idx];
    const { poId, key, increment } = last;

    setSessionScans(prev => {
      const poScans = prev[poId] ?? {};
      return {
        ...prev,
        [poId]: {
          ...poScans,
          [key]: Math.max(0, (poScans[key] ?? 0) - increment),
        },
      };
    });

    setScanHistory(prev => prev.filter((_, i) => i !== idx));
    flashItem(key, true);
  };

  const handleManualIncrement = (key, delta) => {
    const poItems = poItemsMap[selectedPoId]?.items ?? [];
    const item = poItems.find(i => (i.barcodeValue || i.barcode) === key);
    if (!item) return;

    const mode = itemToggles[key] ?? 'single';

    if (delta > 0 && mode === 'carton' && item.cartonSize == null) {
      const sessionCount = sessionScans[selectedPoId]?.[key] ?? 0;
      setCartonPrompt({ item, key, sessionCount });
      return;
    }

    const increment = mode === 'single' ? 1 : (item.cartonSize ?? 1);
    const adjustedDelta = delta > 0 ? increment : -increment;

    const sessionCount = sessionScans[selectedPoId]?.[key] ?? 0;
    const alreadyReceived = item.scannedCount + sessionCount;
    const newTotal = alreadyReceived + adjustedDelta;

    if (newTotal > item.quantity) {
      showToast(`That would exceed the ordered quantity for "${item.barcode}".`, 'warning');
      return;
    }

    setSessionScans(prev => {
      const poScans = prev[selectedPoId] ?? {};
      return {
        ...prev,
        [selectedPoId]: {
          ...poScans,
          [key]: Math.max(0, (poScans[key] ?? 0) + adjustedDelta),
        },
      };
    });

    if (adjustedDelta > 0) setScanHistory(prev => [...prev, { poId: selectedPoId, key, increment: adjustedDelta }]);
    if (adjustedDelta > 0) flashItem(key);
    else if (sessionCount > 0) flashItem(key, true); // nothing to take off when it's already at 0
  };
const handleManualQtyInput = (key, qty) => {
  const poItems = poItemsMap[selectedPoId]?.items ?? [];
  const item = poItems.find(i => (i.barcodeValue || i.barcode) === key);
  if (!item) return;

  const mode = itemToggles[key] ?? 'single';

  // ← ADD THIS: same guard as handleManualIncrement
  if (mode === 'carton' && item.cartonSize == null) {
    const sessionCount = sessionScans[selectedPoId]?.[key] ?? 0;
    setCartonPrompt({ item, key, sessionCount });
    return;
  }

  const increment = mode === 'single' ? 1 : (item.cartonSize ?? 1);
  const adjustedQty = qty * increment;

  const sessionCount = sessionScans[selectedPoId]?.[key] ?? 0;
  const alreadyReceived = item.scannedCount + sessionCount;
  const newTotal = alreadyReceived + adjustedQty;

  if (newTotal > item.quantity) {
    if (alreadyReceived >= item.quantity) {
      showToast(`"${item.barcode}" is already fully received.`, 'warning');
    } else {
      const remaining = item.quantity - alreadyReceived;
      showToast(
        `Scanning a ${setLabel.toLowerCase()} of ${adjustedQty} would exceed the ordered qty. ` +
        `Only ${remaining} unit${remaining !== 1 ? 's' : ''} remaining.`,
        'warning'
      );
    }
    return;
  }

  setSessionScans(prev => {
    const poScans = prev[selectedPoId] ?? {};
    return {
      ...prev,
      [selectedPoId]: {
        ...poScans,
        [key]: (poScans[key] ?? 0) + adjustedQty,
      },
    };
  });

  setScanHistory(prev => [...prev, { poId: selectedPoId, key, increment: adjustedQty }]);
  flashItem(key);
};

  const handleFindPos = async () => {
    if (!scannedBarcode.trim()) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await api.post('/api/receiving/find-pos',
        { barcode: scannedBarcode }, { headers });

      if (!res.data.found) {
        setMatchingPos([]);
      } else {
        const pos = res.data.pos.map(po => ({
          ...po,
          id: po.id,
          number: po.number,
          vendor: po.vendorName,
          itemCount: po.lineItems.length,
          status: po.lineItems.every(li => li.qtyReceived >= li.qtyOrdered)
            ? 'received'
            : po.lineItems.some(li => li.qtyReceived > 0)
            ? 'partial'
            : 'open',
        }));
        setMatchingPos(pos);
      }
      setSelectedPoId(null);
      setPoItemsMap({});

      // Exactly one open PO matches, whether a PO number was typed or an item was scanned:
      // open it straight away instead of making someone tap the only choice.
      if (res.data.found && res.data.pos.length === 1) {
        const only = res.data.pos[0];
        await handleSelectPo({ ...only, id: only.id, number: only.number }, true);
      }
    } catch (err) {
      console.error('Failed to find POs:', err);
      showToast(updateErrorMessage(err, 'Searching for POs failed.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPo = async (po, forceOpen = false) => {
    if (!forceOpen && selectedPoId === po.id) {
      setSelectedPoId(null);
      return;
    }

    setSelectedPoId(po.id);

    if (!forceOpen && poItemsMap[po.id]?.items) return;

    setPoItemsMap(prev => ({ ...prev, [po.id]: { loading: true, items: null } }));

    try {
      const headers = await getAuthHeaders();
      const res = await api.get(`/api/receiving/po-items/${po.id}`, { headers });
      const items = res.data.lineItems.map(li => ({
        barcode: li.itemName,
        barcodeValue: li.barcodeValue,
        quantity: li.qtyOrdered,
        scannedCount: li.qtyBilled,
        location: li.warehouseLocation || 'Location',
        cartonSize: li.cartonSize ?? null,
      }));
      setPoItemsMap(prev => ({ ...prev, [po.id]: { loading: false, items } }));
    } catch (err) {
      console.error('Failed to load PO items:', err);
      showToast(updateErrorMessage(err, 'Loading the PO failed.'));
      setPoItemsMap(prev => ({ ...prev, [po.id]: { loading: false, items: [] } }));
    }
  };

// After a successful update. With no other PO holding unsent scans it goes back to the
// start page as before; otherwise only this PO's scans and photos are cleared, and the list
// of matching POs stays so the others can be carried on and updated in turn.
const finishPo = (po) => {
  const otherUnsent = Object.entries(sessionScans).some(([id, scans]) =>
    id !== String(po.id) && Object.values(scans).some(n => n > 0));
  if (!otherUnsent) { reset(); return; }
  const without = (obj) => { const next = { ...obj }; delete next[po.id]; return next; };
  setSessionScans(without);
  setPoPhotos(without);
  setPoItemsMap(without); // the received amounts changed, so reopening re-reads the PO
  setScanHistory(prev => prev.filter(h => h.poId !== po.id));
  setSelectedPoId(null);
};

const handleFinalizePo = async (po) => {
  try {
    setFinalizingPoId(po.id);
    const headers = await getAuthHeaders();
    const scans = sessionScans[po.id] ?? {};

    // ── 1. Finalize first (creates/updates the bill) ──
    const result = await api.post('/api/receiving/finalize', {
      poId: po.number,
      sessionScansJson: JSON.stringify(scans),
      timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }, { headers });

    const billId = result.data.billId;  // ← get the exact bill ID back

    // ── 2. Upload photos directly to the bill that was just created ──
    const photos = poPhotos[po.id] ?? [];
    if (photos.length > 0 && billId) {
      for (const photo of photos) {
        try {
          await api.post('/api/receiving/upload-photo', {
            billId,                  // ← pass bill ID directly, not PO number
            base64: photo.base64,
            fileName: photo.fileName,
          }, { headers });
        } catch (err) {
          console.warn('Photo upload failed (non-fatal):', err);
        }
      }
    }

    showToast(`PO ${po.number} updated successfully.`);
    finishPo(po);
  } catch (err) {
    console.error('Failed to finalize PO:', err);
    showToast(updateErrorMessage(err), 'error');
  } finally {
    setFinalizingPoId(null);
  }
};

  const handleEditCartonSize = (item, key) => {
    const sessionCount = sessionScans[selectedPoId]?.[key] ?? 0;
    setCartonPrompt({ item, key, sessionCount, edit: true });
  };

  const handleClearCartonSize = (key) => {
    setSessionCartonSizes(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPoItemsMap(prev => ({
      ...prev,
      [selectedPoId]: {
        ...prev[selectedPoId],
        items: prev[selectedPoId].items.map(i =>
          (i.barcodeValue || i.barcode) === key
            ? { ...i, cartonSize: null }
            : i
        ),
      },
    }));
    setItemToggles(prev => ({ ...prev, [key]: 'single' }));
  };

  const handleLocationSubmit = async (location) => {
    const { key } = locationPrompt;
    setLocationPrompt(null);

    setPoItemsMap(prev => ({
      ...prev,
      [selectedPoId]: {
        ...prev[selectedPoId],
        items: prev[selectedPoId].items.map(i =>
          (i.barcodeValue || i.barcode) === key
            ? { ...i, location }
            : i
        ),
      },
    }));

    try {
      const headers = await getAuthHeaders();
      await api.post('/api/items/update-location', {
        barcodeValue: key,
        location,
      }, { headers });
    } catch (err) {
      console.warn('Could not save location to Striven:', err);
      showToast(`The location is set on this screen, but Striven did not save it, so it will show as missing again next time. ${errorReason(err)}`, 'warning');
    }
  };

  const handleCartonSubmit = async (cartonSize) => {
    const { item, key, sessionCount, edit } = cartonPrompt;
    setCartonPrompt(null);

    setSessionCartonSizes(prev => ({ ...prev, [key]: true }));

    setPoItemsMap(prev => ({
      ...prev,
      [selectedPoId]: {
        ...prev[selectedPoId],
        items: prev[selectedPoId].items.map(i =>
          (i.barcodeValue || i.barcode) === key
            ? { ...i, cartonSize }
            : i
        ),
      },
    }));

    // From a scan the carton just scanned counts; from the Edit button only the size changes.
    if (!edit) completeScan({ ...item, cartonSize }, key, sessionCount, cartonSize);

    try {
      const headers = await getAuthHeaders();
      await api.post('/api/items/update-carton-size', {
        barcodeValue: key,
        cartonSize,
      }, { headers });
    } catch (err) {
      console.warn('Could not save carton size to Striven:', err);
      showToast(`The ${setLabel.toLowerCase()} size is set on this screen, but Striven did not save it, so the item will still have the old size next time. ${errorReason(err)}`, 'warning');
    }
  };

  const reset = () => {
    setScannedBarcode('');
    setMatchingPos(null);
    setSelectedPoId(null);
    setPoItemsMap({});
    setSessionScans({});
    setUseCamera(false);
    setSessionCartonSizes({});
    setItemToggles({});
    setScanHistory([]);
    setPoPhotos({});
  };

  selectPoRef.current = handleSelectPo;

  // ── Render ──────────────────────────────────────────────────────────────────
  if (reportUrlLoading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
        <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
      </div>
    );
  }

if (accessDenied) {
  return (
    <div style={{
      maxWidth: 480,
      margin: "4rem auto",
      padding: "2rem",
      background: "var(--surface)",
      borderRadius: 12,
      border: "1px solid var(--border)",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 36, marginBottom: "1rem" }}>🔒</div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: "0.5rem", color: "var(--text)" }}>
        PO Receiving not enabled
      </h2>
      <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>
        This feature is currently a work in progress. Check back soon!
      </p>
    </div>
  );
}
  if (!reportUrl) {
    return (
      <div style={{
        maxWidth: 480,
        margin: "4rem auto",
        padding: "2rem",
        background: "var(--surface)",
        borderRadius: 12,
        border: "1px solid var(--border)",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 36, marginBottom: "1rem" }}>📋</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: "0.5rem", color: "var(--text)" }}>
          Report URL not configured
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>
          An admin needs to add the PO Receiving report URL in the{" "}
          <strong>Admin Panel → Credentials</strong> tab before this feature can be used.
        </p>
      </div>
    );
  }

  const isDisabled = finalizingPoId !== null;

  return (
    <div style={s.page}>
      {/* Hidden file input for photo capture */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: 'none' }}
        onChange={handlePhotoFileChange}
      />

      <div style={s.pageHeader}>
        <h1 style={s.title}>PO Receiving</h1>
        <p style={s.subtitle}>Scan an item to find its open POs, then receive and shelve.</p>
      </div>

      {/* ── Scan / Find POs ── */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Scan item</div>
        <div style={s.scanRow}>
          <input
            style={s.input}
            disabled={isDisabled}
            type="text"
            value={selectedPoId ? receivingInput : scannedBarcode}
            onChange={e => {
              if (selectedPoId) {
                setReceivingInput(e.target.value);
              } else {
                setScannedBarcode(e.target.value);
              }
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              if (selectedPoId) {
                handleReceivingScan(receivingInput);
                setReceivingInput('');
              } else {
                handleFindPos();
              }
            }}
            placeholder={
              selectedPoId
                ? 'Scan or type item barcode/name to receive…'
                : 'Scan an item barcode, or type a PO number…'
            }
            autoFocus
          />
          <button
            style={s.greenBtn}
            onClick={async () => {
              if (useCamera && cameraRef.current) await cameraRef.current.stopScanner();
              setUseCamera(prev => !prev);
            }}
          >
            {useCamera ? 'Manual' : 'Camera'}
          </button>
          {selectedPoId ? (
            <button
              style={s.greenBtn}
              onClick={() => {
                handleReceivingScan(receivingInput);
                setReceivingInput('');
              }}
              disabled={!receivingInput.trim()}
            >
              Receive Item
            </button>
          ) : (
            <button style={s.greenBtn} onClick={handleFindPos} disabled={loading}>
              {loading ? 'Searching…' : 'Find POs'}
            </button>
          )}
        </div>

        {useCamera && (
          <div style={{ marginTop: 12 }}>
            <CameraScan ref={cameraRef} onDetected={handleCamScan} />
          </div>
        )}
      </div>

      {/* ── Matching POs ── */}
      {matchingPos !== null && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Matching open POs</div>

          {matchingPos.length === 0 ? (
            <div style={s.empty}>No open POs found for this item.</div>
          ) : (
            matchingPos.map(po => {
              const isOpen = selectedPoId === po.id;
              const poState = poItemsMap[po.id];
              const poItems = poState?.items ?? [];
              const isLoadingItems = poState?.loading ?? false;
              const poSession = sessionScans[po.id] ?? {};
              const totalSessionScans = Object.values(poSession).reduce((a, b) => a + b, 0);
              const isDisabled = finalizingPoId !== null;
              const photos = poPhotos[po.id] ?? [];
              const canAddPhoto = photos.length < maxPhotosPerItem;

              return (
                <div key={po.id} style={s.poWrapper}>

                  {/* ── PO header row ── */}
                  <div
                    style={{ ...s.poCard, ...(isOpen ? s.poCardSelected : {}), marginBottom: 0 }}
                    onClick={() => handleSelectPo(po)}
                  >
                    <div>
                      <div style={s.poId}>{po.number}</div>
                      <div style={s.poMeta}>{po.vendor} · {po.itemCount} items expected</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {isOpen && scanHistory.some(h => h.poId === po.id) && (
                        <button
                          style={{ ...s.undoBtn, padding: '4px 12px', fontSize: 12 }}
                          onClick={e => { e.stopPropagation(); handleUndo(); }}
                          disabled={isDisabled}
                        >
                          ↩ Undo ({scanHistory.filter(h => h.poId === po.id).length})
                        </button>
                      )}
                      {totalSessionScans > 0 && (
                        <span style={isOpen ? s.sessionBadge : { ...s.sessionBadge, background: '#fef3c7', color: '#92400e' }}>
                          {isOpen ? `${totalSessionScans} scanned this session` : `${totalSessionScans} scanned, not sent yet`}
                        </span>
                      )}
                      <span style={{ ...s.badge, ...(po.status === 'open' ? s.badgeOpen : s.badgePartial) }}>
                        {po.status === 'open' ? 'Open' : 'Partially received'}
                      </span>
                      <span style={{ ...s.chevron, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                        ▾
                      </span>
                    </div>
                  </div>

                  {/* ── Expanded items panel ── */}
                  {isOpen && (
                    <div style={s.expandedPanel}>
                      <div style={s.expandedInner}>

                        {/* ── Back to the list of matching POs; scans on this PO are kept ── */}
                        <div style={s.backRow}>
                          <button style={s.backBtn} onClick={() => setSelectedPoId(null)} disabled={isDisabled}>
                            ← Back to matching POs
                          </button>
                          {totalSessionScans > 0 && <span style={s.backNote}>Your scans on this PO are kept.</span>}
                        </div>

                        {/* ── Photo strip ── */}
                        <div style={s.photoStrip}>
                          <div style={s.photoStripLabel}>
                            <span>📷 Delivery photos</span>
                            <span style={s.photoStripCount}>
                              {photos.length}/{maxPhotosPerItem}
                            </span>
                          </div>

                          <div style={s.photoThumbnails}>
                            {photos.map((photo, idx) => (
                              <div key={idx} style={s.thumbWrapper}>
                                <img
                                  src={photo.dataUrl}
                                  alt={`Delivery photo ${idx + 1}`}
                                  style={s.thumb}
                                />
                                <button
                                  style={s.thumbRemove}
                                  onClick={() => handleRemovePhoto(po.id, idx)}
                                  disabled={isDisabled}
                                  title="Remove photo"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}

                            {canAddPhoto && (
                              <button
                                style={s.addPhotoBtn}
                                onClick={() => handlePhotoCapture(po.id)}
                                disabled={isDisabled}
                                title="Add delivery photo"
                              >
                                <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
                                <span style={{ fontSize: 11, marginTop: 2 }}>Photo</span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={s.divider} />

                        {isLoadingItems ? (
                          <div style={s.empty}>Loading items…</div>
                        ) : poItems.length === 0 ? (
                          <div style={s.empty}>No items found.</div>
                        ) : (
                          poItems.map(item => {
                            const key = item.barcodeValue || item.barcode;
                            const sessionCount = poSession[key] ?? 0;
                            const rowFlash = flash.key === key
                              ? (flash.down ? (flash.n % 2 ? 'po-flash-down-a' : 'po-flash-down-b') : (flash.n % 2 ? 'po-flash-a' : 'po-flash-b'))
                              : '';
                            const isOverReceived = item.scannedCount + sessionCount > item.quantity;

                            return (
                              <div
                                key={key}
                                ref={el => { if (el) itemRowRefs.current[key] = el; else delete itemRowRefs.current[key]; }}
                                className={rowFlash}
                                style={s.itemRow}
                              >

                                {/* ── ROW 1: pill + name ── */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {(() => {
                                    const total = item.scannedCount + sessionCount;
                                    const isComplete = total >= item.quantity;
                                    const isStarted = total > 0;
                                    const pill = isComplete
                                      ? { label: 'Complete',    bg: '#dcfce7', color: '#166534', dot: '#16a34a' }
                                      : isStarted
                                      ? { label: 'Partial',     bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' }
                                      : { label: 'Not started', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' };
                                    return (
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                                        background: pill.bg, color: pill.color, flexShrink: 0, whiteSpace: 'nowrap',
                                        width: 90, justifyContent: 'center',
                                      }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: pill.dot, flexShrink: 0 }} />
                                        {pill.label}
                                      </span>
                                    );
                                  })()}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <div style={s.itemName}>{item.barcode}</div>
                                    {isOverReceived && <span style={s.overBadge}>Over</span>}
                                    {sessionCount > 0 && (
                                      <span style={{
                                        ...s.sessionTally,
                                        ...(isOverReceived ? s.sessionTallyOver : {}),
                                        fontSize: 14, padding: '3px 12px', flexShrink: 0,
                                      }}>
                                        +{sessionCount}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* ── ROW 2: progress+location left, controls right ── */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

                                  {/* Left: location + progress bar + count */}
                                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {showWarehouseLocation && (
item.location && item.location !== 'Location'
  ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={s.locationLine}>{item.location}</div>
      <button
        style={s.locationEditBtn}
        onClick={e => { e.stopPropagation(); setLocationPrompt({ item, key }); }}
        disabled={isDisabled}
        title="Edit location"
      >
        ✏️
      </button>
    </div>
  )
                                        : (
                                          <button
                                            style={s.missingLocationBtn}
                                            onClick={e => { e.stopPropagation(); setLocationPrompt({ item, key }); }}
                                            disabled={isDisabled}
                                          >
                                            ⚠ Missing location — click to add
                                          </button>
                                        )
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
                                        <div style={{
                                          height: '100%', borderRadius: 4, transition: 'width 0.3s',
                                          width: `${Math.min(100, ((item.scannedCount + sessionCount) / item.quantity) * 100)}%`,
                                          background: (item.scannedCount + sessionCount) >= item.quantity
                                            ? '#166534'
                                            : (item.scannedCount + sessionCount) > 0
                                            ? 'rgb(202, 187, 54)'
                                            : '#639922',
                                        }} />
                                      </div>
                                      <span style={{
                                        fontSize: 13, fontWeight: 600, flexShrink: 0,
                                        color: (item.scannedCount + sessionCount) >= item.quantity ? '#166534' : '#6b7280',
                                      }}>
                                        {item.scannedCount + sessionCount} / {item.quantity}
                                        {(item.scannedCount + sessionCount) >= item.quantity ? ' ✓' : ''}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Right: carton label + toggle + adjust */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexShrink: 0 }}>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <button
                                        style={item.cartonSize != null ? s.cartonEditBtn : { ...s.cartonEditBtn, ...s.cartonSetBtn }}
                                        onClick={() => handleEditCartonSize(item, key)}
                                        disabled={isDisabled}
                                      >
                                        {item.cartonSize != null
                                          ? `${setLabel}: ${item.cartonSize} · Edit`
                                          : `Set ${setLabel.toLowerCase()} size`}
                                      </button>
                                      {sessionCartonSizes[key] && (
                                        <button style={s.cartonClearBtn} title="Remove carton size" onClick={() => handleClearCartonSize(key)}>✕</button>
                                      )}
                                    </div>

                                    <div style={s.toggleWrap}>
                                      <button
                                        style={{ ...s.toggleBtn, ...((itemToggles[key] ?? 'single') === 'single' ? s.toggleActive : {}) }}
                                        onClick={() => setItemToggles(prev => ({ ...prev, [key]: 'single' }))}
                                        disabled={isDisabled}
                                      >Single</button>
                                      <button
                                        style={{ ...s.toggleBtn, ...((itemToggles[key] ?? 'single') === 'carton' ? s.toggleActive : {}) }}
                                        onClick={() => setItemToggles(prev => ({ ...prev, [key]: 'carton' }))}
                                        disabled={isDisabled}
                                      >{setLabel}</button>
                                    </div>

<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
  <button className="po-adjust-btn up" style={s.adjustBtn} onClick={() => handleManualIncrement(key, 1)} disabled={isDisabled} title="Increase">⬆</button>
  <button className="po-adjust-btn down" style={s.adjustBtn} onClick={() => handleManualIncrement(key, -1)} disabled={isDisabled} title="Decrease">⬇</button>
  <input
    type="number"
    placeholder="Qty"
    min="1"
    style={s.adjustInput}
    ref={el => adjustInputRefs.current[key] = el}
    disabled={isDisabled}
    onKeyDown={e => {
      if (e.key !== 'Enter') return;
      const qty = parseInt(e.target.value, 10);
      if (!isNaN(qty) && qty > 0) {
        handleManualQtyInput(key, qty);
        e.target.value = '';
      }
    }}
  />
  {/* ← ADD: confirm button for tablet/touch users */}
  <button
    className="po-adjust-btn up"
    style={s.adjustBtn}
    disabled={isDisabled}
    title="Confirm quantity"
    onClick={() => {
      const input = adjustInputRefs.current[key];
      const qty = parseInt(input?.value, 10);
      if (!isNaN(qty) && qty > 0) {
        handleManualQtyInput(key, qty);
        if (input) input.value = '';
      }
    }}
  >
    +
  </button>
</div>

                                  </div>
                                </div>

                              </div>
                            );
                          })
                        )}

                        <div style={s.divider} />
                        <div style={s.actionRow}>
                          <button
                            style={s.ghostBtn}
                            onClick={() => {
                              const unsent = Object.values(sessionScans).reduce(
                                (sum, scans) => sum + Object.values(scans).reduce((a, b) => a + b, 0), 0);
                              if (unsent > 0 && !window.confirm(`Start over? This throws away ${unsent} unsent scanned unit${unsent === 1 ? '' : 's'}, on every PO.`)) return;
                              reset();
                            }}
                            disabled={isDisabled}
                          >
                            Start over
                          </button>

                          <button
                            style={{
                              ...s.greenBtn,
                              marginLeft: 'auto',
                              ...(totalSessionScans === 0 || finalizingPoId === po.id ? s.btnDisabled : {}),
                              ...(isDisabled && finalizingPoId !== po.id ? s.btnDisabled : {}),
                            }}
                            onClick={() => handleFinalizePo(po)}
                            disabled={totalSessionScans === 0 || isDisabled}
                          >
                            {finalizingPoId === po.id
                              ? '⏳ Updating...'
                              : `Update PO${totalSessionScans > 0 ? ` (${totalSessionScans})` : ''}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Carton size modal ── */}
      {cartonPrompt && (
        <div style={s.modalOverlay}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>{cartonPrompt.edit ? `Edit ${setLabel.toLowerCase()} size` : `${setLabel} size unknown`}</div>
            <div style={s.modalSub}>
              How many units are in a {setLabel.toLowerCase()} of <strong>{cartonPrompt.item.barcode}</strong>?
            </div>
            <input
              ref={cartonInputRef}
              type="number"
              min="1"
              autoFocus
              defaultValue={cartonPrompt.edit ? (cartonPrompt.item.cartonSize ?? '') : undefined}
              style={s.modalInput}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                const val = parseInt(cartonInputRef.current?.value, 10);
                if (!val || val < 1) return;
                handleCartonSubmit(val);
              }}
            />
            <div style={s.modalActions}>
<button style={s.ghostBtn} onClick={() => {
  const { item, key, sessionCount, edit } = cartonPrompt;
  setCartonPrompt(null);
  if (edit) return; // just closing the editor
  setItemToggles(prev => ({ ...prev, [key]: 'single' })); 
  completeScan(item, key, sessionCount, 1);
}}>
  {cartonPrompt.edit ? 'Cancel' : 'Skip (count as 1)'}
</button>
              <button style={s.greenBtn} onClick={() => {
                const val = parseInt(cartonInputRef.current?.value, 10);
                if (!val || val < 1) return;
                handleCartonSubmit(val);
              }}>
                {cartonPrompt.edit ? 'Save' : 'Save & scan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Location modal ── */}
      {locationPrompt && (
        <div style={s.modalOverlay}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>Add warehouse location</div>
            <div style={s.modalSub}>
              Where should <strong>{locationPrompt.item.barcode}</strong> be shelved?
            </div>
            <input
              ref={locationInputRef}
              type="text"
              placeholder="e.g. Aisle 3 · Shelf B"
              autoFocus
              style={s.modalInput}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                const val = locationInputRef.current?.value?.trim();
                if (!val) return;
                handleLocationSubmit(val);
              }}
            />
            <div style={s.modalActions}>
              <button style={s.ghostBtn} onClick={() => setLocationPrompt(null)}>
                Cancel
              </button>
              <button style={s.greenBtn} onClick={() => {
                const val = locationInputRef.current?.value?.trim();
                if (!val) return;
                handleLocationSubmit(val);
              }}>
                Save location
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Alert modal ── */}
      {alert && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 3000,
        }} onClick={() => setAlert(null)}>
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: '28px 32px',
              width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              textAlign: 'center',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>
              {alert.type === 'success' ? '✓' : alert.type === 'warning' ? '⚠️' : '❌'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 8 }}>
              {alert.type === 'success' ? 'Done' : alert.type === 'warning' ? 'Alert' : 'Error'}
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: alert.type === 'error' ? 'left' : 'center' }}>
              {alert.message}
            </div>
            <button
              autoFocus
              onClick={() => setAlert(null)}
              style={{
                padding: '9px 28px',
                background: alert.type === 'success' ? '#16a34a'
                          : alert.type === 'warning' ? '#d97706' : '#dc2626',
                color: '#fff', border: 'none', borderRadius: 6,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* Same timing and colours as the Sales Order row flash (Table.css), as solid colours
           because these rows sit on a white background */
        .po-flash-a { animation: poFlashA 0.9s ease-out; }
        .po-flash-b { animation: poFlashB 0.9s ease-out; }
        .po-flash-down-a { animation: poFlashDownA 0.9s ease-out; }
        .po-flash-down-b { animation: poFlashDownB 0.9s ease-out; }
        @keyframes poFlashA { from { background-color: rgb(204, 246, 219); } to { background-color: #fff; } }
        @keyframes poFlashB { from { background-color: rgb(204, 246, 219); } to { background-color: #fff; } }
        @keyframes poFlashDownA { from { background-color: rgb(253, 218, 218); } to { background-color: #fff; } }
        @keyframes poFlashDownB { from { background-color: rgb(253, 218, 218); } to { background-color: #fff; } }
        @media (prefers-reduced-motion: reduce) {
          .po-flash-a, .po-flash-b, .po-flash-down-a, .po-flash-down-b { animation: none; }
          .po-adjust-btn, .po-adjust-btn:hover, .po-adjust-btn:active { transform: none; }
        }
        /* Arrow buttons: green up, red down, always on (the tablet has no hover). Pressing or
           hovering deepens the colour. */
        .po-adjust-btn {
          background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;
          transition: background 0.12s, color 0.12s, border-color 0.12s;
        }
        .po-adjust-btn.up { color: #15803d; border-color: #22c55e; background: #f0fdf4; }
        .po-adjust-btn.down { color: #991b1b; border-color: #ef4444; background: #fef2f2; }
        /* Hover: lifts a touch with a soft shadow. Press: sinks in and goes a shade deeper.
           Same as the Sales Order arrows. */
        .po-adjust-btn { transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease; }
        .po-adjust-btn.up:hover:not(:disabled)   { background: #dcfce7; transform: translateY(-1px); box-shadow: 0 3px 8px rgba(34, 197, 94, 0.28); }
        .po-adjust-btn.down:hover:not(:disabled) { background: #fee2e2; transform: translateY(-1px); box-shadow: 0 3px 8px rgba(239, 68, 68, 0.26); }
        .po-adjust-btn.up:active:not(:disabled)   { background: #bbf7d0; transform: translateY(0) scale(0.92); box-shadow: none; }
        .po-adjust-btn.down:active:not(:disabled) { background: #fecaca; transform: translateY(0) scale(0.92); box-shadow: none; }
        .po-adjust-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

const s = {
  page: { padding: '32px 24px', maxWidth: 900, margin: '0 auto' },
  pageHeader: { marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 700, color: '#14532d', marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: 0 },
  section: { background: '#fff', border: '1px solid #bbf7d0', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 },
  scanRow: { display: 'flex', gap: 8 },
  input: { flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 },
  greenBtn: { padding: '8px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  ghostBtn: { padding: '8px 18px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  empty: { fontSize: 14, color: '#9ca3af', padding: '12px 0' },
  poWrapper: { marginBottom: 8 },
  poCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  poCardSelected: {
    border: '1.5px solid #d1d5db',
    background: 'var(--color-background-secondary, #f8f9fa)',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: '1px solid #e5e7eb',
  },
  poId: { fontSize: 15, fontWeight: 600, color: '#111' },
  poMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  badge: { fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600 },
  badgeOpen: { background: '#dbeafe', color: '#1e40af' },
  badgePartial: { background: '#fef3c7', color: '#92400e' },
  sessionBadge: { fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: '#dbeafe', color: '#1e40af' },
  chevron: { fontSize: 16, color: '#6b7280', transition: 'transform 0.2s', display: 'inline-block', lineHeight: 1 },
  expandedPanel: {
    border: '1.5px solid #d1d5db',
    borderTop: 'none',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    background: '#f8f9fa',
  },
  expandedInner: { padding: '16px 14px' },

  // ── Photo strip ──
  backRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  backBtn: { padding: '8px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  backNote: { fontSize: 12, color: '#6b7280' },
  photoStrip: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 4,
  },
  photoStripLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 12,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 10,
  },
  photoStripCount: {
    fontSize: 11,
    fontWeight: 600,
    color: '#9ca3af',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 20,
    padding: '2px 8px',
  },
  photoThumbnails: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  thumbWrapper: {
    position: 'relative',
    width: 72,
    height: 72,
    borderRadius: 6,
    overflow: 'visible',
    flexShrink: 0,
  },
  thumb: {
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    display: 'block',
  },
  thumbRemove: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    padding: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  addPhotoBtn: {
    width: 72,
    height: 72,
    borderRadius: 6,
    border: '1.5px dashed #d1d5db',
    background: '#f9fafb',
    color: '#9ca3af',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    fontWeight: 600,
    transition: 'border-color 0.15s, color 0.15s',
    flexShrink: 0,
  },

  cartonEditBtn: {
    fontSize: 11, fontWeight: 600, color: '#166534',
    background: '#dcfce7', border: '1px solid #bbf7d0',
    borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
    whiteSpace: 'nowrap', lineHeight: 1.4,
  },
  cartonSetBtn: { color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db' },
  cartonClearBtn: {
    fontSize: 11, fontWeight: 700, color: '#92400e',
    background: '#fef3c7', border: '1px solid #fde68a',
    borderRadius: 4, padding: '3px 6px', cursor: 'pointer',
    lineHeight: 1.4,
  },
  missingLocationBtn: {
    fontSize: 14,
    color: '#92400e', background: 'transparent',
    border: 'none', padding: 0, cursor: 'pointer',
    textDecoration: 'underline', textUnderlineOffset: 2,
    lineHeight: '1.4',
    display: 'block',
    textAlign: 'left',
  },
  itemName: { fontSize: 14, fontWeight: 600, color: '#111' },
  progressRow: { display: 'flex', alignItems: 'center', gap: 8 },
  progressBg: { flex: 1, height: 6, borderRadius: 3, background: '#d1fae5', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s' },
  progressLabel: {
    fontSize: 12,
    color: '#6b7280',
    width: 80,
    minWidth: 80,
    textAlign: 'right',
    flexShrink: 0,
  },
  adjustBtn: {
    width: 40, height: 40, fontSize: 25, borderRadius: 6,
    cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontWeight: 1500,
  },
  adjustInput: {
    width: 64, height: 40, padding: '6px 8px',
    border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 14, textAlign: 'center',
  },
  sessionTally: { fontSize: 13, fontWeight: 700, color: '#1e40af', background: '#dbeafe', borderRadius: 20, padding: '2px 10px', minWidth: 32, textAlign: 'center', border: '1px solid #bfdbfe', flexShrink: 0 },
  sessionTallyOver: { color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a' },
  overBadge: { fontSize: 10, fontWeight: 600, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 6px' },
  locationTag: { fontSize: 12, background: '#dcfce7', color: '#166534', borderRadius: 6, padding: '3px 8px', border: '1px solid #bbf7d0', whiteSpace: 'nowrap' },
  divider: { height: 1, background: '#d1fae5', margin: '16px 0' },
  actionRow: { display: 'flex', gap: 8 },
  locationLine: {
    fontSize: 14, color: '#6b7280', marginBottom: 0, textAlign: 'left',
    lineHeight: '1.4', display: 'block',
  },
  undoBtn: { padding: '8px 18px', background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: 12, padding: '24px 28px', width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 6 },
  modalSub: { fontSize: 14, color: '#6b7280', marginBottom: 16 },
  modalInput: { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 16, marginBottom: 16, boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  toggleWrap: {
    display: 'flex', borderRadius: 6,
    border: '1px solid #d1d5db',
    overflow: 'hidden', flexShrink: 0,
  },
  toggleBtn: {
    padding: '8px 18px', fontSize: 13, fontWeight: 600,
    background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280',
  },
  toggleActive: { background: '#616e5f', color: '#fff' },
  itemRow: {
    display: 'flex', flexDirection: 'column', gap: 8,
    padding: '14px 14px', marginBottom: 4,
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 6,
  },
  locationEditBtn: {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  padding: '0 2px',
  lineHeight: 1,
  opacity: 0.8,
  transition: 'opacity 0.15s',
  flexShrink: 0,
},
};